import { Hidden } from '@material-ui/core';
import React, { useEffect } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { useDeserializedToken } from '../../../pages/arc/components/use-deserialized-token';
import { PortalLandingBanner } from '../../../pages/arc/features/map-landing/components/portal-landing-banner';
import { useGroupsReset } from '../../../pages/arc/hooks/useGroupsReset';
import { usePersonManagementReset, useSearchListsReset } from '../../../pages/arc/hooks/usePersonManagementReset';
import { MapPortalProps } from '../../../pages/arc/models/arc-epi-props';
import { TcsmStates } from '../../../root/tcsm-model';
import { setNavigationContext, setUserContext } from '../../ducks/app-context';
import { removeForbiddenFeature } from '../../ducks/authenticated-slice';
import { NavigationContext, UserContext } from '../../models/app-context-model';
import { LabelSettingProps, TcsmSiteSettingsProps } from '../../models/epi-model';
import { TcsmFeatures } from '../../models/features-model';
import { TcsmLandingPage } from '../../models/tcsm-page-models';
import { BlockFactory } from '../block-factory';
import { GuidedProcessSupportBlock } from '../guided-process-support-block';
import { DividerSection } from '../layout/divider-section';
import { MainContent } from '../layout/main-content-container';
import { PageContainer } from '../layout/page-container';
import { PageHeader } from '../layout/page-header';
import { SideBarContent } from '../layout/sidebar-content';
import { WysiwygWithReactLink } from '../wysiwyg-with-react-link';
import { ContentSpacer } from '../layout/generic-content-spacer';
import { QueryArrayMessage } from '../messages/query-array-message';
import { stripHtmlAndCleanText, stripWrappingNbsp } from '../../helpers/html-to-string-helpers';

interface Props {
	pageData: TcsmLandingPage,
}

export const TcsmLandingPageView = (props: Props) => {
	const { pageData } = props;
	const portalProps = useSelector<TcsmStates, MapPortalProps>(state => state.epiProps?.mapPortalSettingsState?.epiContent);
	const roles = useSelector<TcsmStates, string[]>(state => state?.authenticatedState?.roles);
	const siteSettings = useSelector<TcsmStates, TcsmSiteSettingsProps>(state => state.epiProps?.siteSettingsState?.siteSettings);
  const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
  const mapBillPayNotificationLabel = stripHtmlAndCleanText(labelSettings?.mapBillPayNotificationLabel);
  const mapBillPayNotificationDescription = stripWrappingNbsp(labelSettings?.mapBillPayNotificationDescription);

	const dispatch = useDispatch();
	useEffect(() => {
		dispatch(removeForbiddenFeature(TcsmFeatures.InstitutionAdministratorSearch));
		dispatch(setNavigationContext(NavigationContext.None));
		if (pageData?.showPortalBanner) {
			dispatch(setUserContext(UserContext.MINIMAP));
		}
	}, []);
	usePersonManagementReset();
	useGroupsReset();
	useSearchListsReset();

	const token = useDeserializedToken();
	const name = `${token?.person?.fn} ${token?.person?.ln}`;
	let matchedRoles = portalProps?.roleNameMapping?.filter(a => roles?.includes(a.key))?.map(a => a?.value);

	if (!matchedRoles) {
		matchedRoles = [];
	}
	let roleMessage = matchedRoles?.length > 1 ?
		portalProps?.multipleAdminRoleText?.replace('{role}', matchedRoles?.join(', ')) :
		portalProps?.adminRoleText?.replace('{role}', matchedRoles[0]);

	if (matchedRoles?.length === 0) {
		roleMessage = '';
	}

	if (!portalProps && pageData?.showPortalBanner) {
		return <LoadingCircle />;
	}

  return (
		<PageContainer>
			{
        pageData?.showPortalBanner &&
				<PortalLandingBanner
        userFullName={name}
        userRole={roleMessage}
        userGreeting={siteSettings?.greetingText}
        brandImage={portalProps?.brandImage}
        brandImageAltText={portalProps?.brandImageAltText}
        bannerImageUrl={portalProps?.bannerImageLink}
        bannerImageAltText={portalProps?.bannerImageAltText}
        bannerImage={portalProps?.bannerImage} />
			}
			<MainContent>
        {mapBillPayNotificationLabel && mapBillPayNotificationDescription && (
          <ContentSpacer>
            <Banner
              banner={{
                label: mapBillPayNotificationLabel,
                description: <WysiwygWithReactLink content={mapBillPayNotificationDescription} />,
                type: 'info',
              }}
            />
          </ContentSpacer>
        )}
				<QueryArrayMessage
					queryKey='success'
					useWarnings={[]}
				/>
				<PageHeader
					headerText={pageData?.pageTitle}
				/>
				<Hidden mdUp>
					<SideBarContent>
						<div className='sidebar'>
							{
								(pageData?.sideBarOptionalContent || pageData?.sideBarOptionalContentTitle) &&
								<div className='sidebar-section'>
									<ContentSpacer>
											<h2>{pageData?.sideBarOptionalContentTitle}</h2>
									</ContentSpacer>
									<WysiwygWithReactLink content={pageData?.sideBarOptionalContent} />
								</div>
							}
						</div>
						<GuidedProcessSupportBlock title={pageData?.landingPageGpsTitle} contents={pageData?.landingPageGps} />
					</SideBarContent>
				</Hidden>
				{
					pageData?.linkSectionContent?.map((a, idx) => <BlockFactory key={idx} blockData={a} />)
				}
				<DividerSection removeDivider sectionText={pageData?.guidesAndResourcesTitle}>
					<div className='dashboard-resources-container'>
						{
							pageData?.guidesAndResourceContent?.map((a, idx) => <BlockFactory key={idx} blockData={a} />)
						}
					</div>
				</DividerSection>
				<WysiwygWithReactLink content={pageData?.guidesAndResourcesOptionalDescription} />
			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<div className='sidebar'>
						{
							(pageData?.sideBarOptionalContent || pageData?.sideBarOptionalContentTitle) &&
							<div className='sidebar-section'>
								<ContentSpacer>
										<h2>{pageData?.sideBarOptionalContentTitle}</h2>
								</ContentSpacer>
								<WysiwygWithReactLink content={pageData?.sideBarOptionalContent} />
							</div>
						}
					</div>
					<GuidedProcessSupportBlock title={pageData?.landingPageGpsTitle} contents={pageData?.landingPageGps} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);

};
